import { PaintStatus } from './VehiclePaintMeasurement.types';

/**
 * Konfiguracja kolorów dla różnych statusów pomiaru lakieru
 */
export interface PaintStatusConfig {
  /** Kolor wypełnienia obszaru */
  fillColor: string;
  /** Kolor obramowania */
  strokeColor: string;
  /** Kolor tekstu etykiety */
  textColor: string;
  /** Opis statusu */
  label: string;
  /** Zakres wartości (dla legendy) */
  range: string;
}

/**
 * Mapowanie statusów na konfigurację kolorów i opisów
 */
export const paintStatusConfig: Record<PaintStatus, PaintStatusConfig> = {
  RANGE_0_50: {
    fillColor: '#148B4D', // zielony (0-100), fill z opacity 50%
    strokeColor: '#148B4D',
    textColor: '#ffffff',
    label: '0-50 μm',
    range: '0-100',
  },
  RANGE_50_100: {
    fillColor: '#148B4D', // zielony (0-100), fill z opacity 50%
    strokeColor: '#148B4D',
    textColor: '#ffffff',
    label: '50-100 μm',
    range: '0-100',
  },
  RANGE_100_150: {
    fillColor: '#73C69B', // jasny zielony (100-200), fill z opacity 50%
    strokeColor: '#73C69B',
    textColor: '#ffffff',
    label: '100-150 μm',
    range: '100-200',
  },
  RANGE_150_200: {
    fillColor: '#73C69B', // jasny zielony (100-200), fill z opacity 50%
    strokeColor: '#73C69B',
    textColor: '#ffffff',
    label: '150-200 μm',
    range: '100-200',
  },
  RANGE_200_250: {
    fillColor: '#FFD91B', // żółty (200-400), fill z opacity 50%
    strokeColor: '#FFD91B',
    textColor: '#ffffff',
    label: '200-250 μm',
    range: '200-400',
  },
  RANGE_250_300: {
    fillColor: '#FFD91B', // żółty (200-400), fill z opacity 50%
    strokeColor: '#FFD91B',
    textColor: '#ffffff',
    label: '250-300 μm',
    range: '200-400',
  },
  RANGE_300_400: {
    fillColor: '#FFD91B', // żółty (200-400), fill z opacity 50%
    strokeColor: '#FFD91B',
    textColor: '#ffffff',
    label: '300-400 μm',
    range: '200-400',
  },
  RANGE_400_900: {
    fillColor: '#FE6728', // pomarańczowy (400-900), fill z opacity 50%
    strokeColor: '#FE6728',
    textColor: '#ffffff',
    label: '400-900 μm',
    range: '400-900',
  },
  RANGE_900_PLUS: {
    fillColor: '#EE2A2A', // czerwony (900+), fill z opacity 50%
    strokeColor: '#EE2A2A',
    textColor: '#ffffff',
    label: '900+ μm',
    range: '900+',
  },
  MISSING: {
    fillColor: '#6b7280', // gray-500
    strokeColor: '#9ca3af', // gray-400
    textColor: '#ffffff',
    label: 'Brak',
    range: 'BRAK',
  },
  NOT_APPLICABLE: {
    fillColor: '#4b5563', // gray-600
    strokeColor: '#6b7280', // gray-500
    textColor: '#ffffff',
    label: 'Nie dotyczy',
    range: 'BRAK',
  },
};

/**
 * Określa status pomiaru na podstawie wartości w μm
 * @param value - wartość pomiaru w μm
 * @param notApplicable - czy pomiar nie ma zastosowania (element nie istnieje)
 */
export const getPaintStatusFromValue = (value?: number, notApplicable?: boolean): PaintStatus => {
  if (notApplicable) {
    return 'NOT_APPLICABLE';
  }

  if (value === undefined || value === null) {
    return 'MISSING';
  }

  if (value >= 0 && value < 50) {
    return 'RANGE_0_50';
  }

  if (value >= 50 && value < 100) {
    return 'RANGE_50_100';
  }

  if (value >= 100 && value < 150) {
    return 'RANGE_100_150';
  }

  if (value >= 150 && value < 200) {
    return 'RANGE_150_200';
  }

  if (value >= 200 && value < 250) {
    return 'RANGE_200_250';
  }

  if (value >= 250 && value < 300) {
    return 'RANGE_250_300';
  }

  if (value >= 300 && value < 400) {
    return 'RANGE_300_400';
  }

  if (value >= 400 && value < 900) {
    return 'RANGE_400_900';
  }

  // value >= 900
  return 'RANGE_900_PLUS';
};

/**
 * Formatuje zakres min-max pomiaru lakieru.
 * Dla wartości >= 900 wyświetla "900+" zamiast np. "900-900".
 */
export const formatMeasurementRange = (
  minMeasurement: number,
  maxMeasurement: number,
): string => {
  if (minMeasurement >= 900) {
    return '900+';
  }
  return `${minMeasurement}-${maxMeasurement}`;
};
